<template>
  <div v-if="showDemoMsg" class="demo-disclaimer demo-disclaimer--desktop">
    This site is intended for demonstration purposes only.
  </div>
  <div v-if="showDemoMsg" class="demo-disclaimer-mobile" ref="demoMobile">
    <button
      type="button"
      class="demo-info-btn"
      aria-label="Demonstration site notice"
      :aria-expanded="showDemoTooltip"
      @click.stop="toggleDemoTooltip"
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
      </svg>
    </button>
    <div
      v-if="showDemoTooltip"
      class="demo-tooltip"
      role="tooltip"
    >
      This site is intended for demonstration purposes only.
    </div>
  </div>
</template>

<script>
export default {
  name: "DemoDisclaimer",
  data() {
    return {
      showDemoMsg: process.env.VUE_APP_SHOW_DEMO_DISCLAIMER === 'true',
      showDemoTooltip: false
    };
  },
  mounted() {
    document.addEventListener('click', this.handleDemoTooltipOutside);
  },
  unmounted() {
    document.removeEventListener('click', this.handleDemoTooltipOutside);
  },
  methods: {
    toggleDemoTooltip() {
      this.showDemoTooltip = !this.showDemoTooltip;
    },
    handleDemoTooltipOutside(event) {
      if (!this.showDemoTooltip) {
        return;
      }
      const wrap = this.$refs.demoMobile;
      if (wrap && !wrap.contains(event.target)) {
        this.showDemoTooltip = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
@use "sass:math";
@import "../assets/scss/distributorVariables/variable.scss";

// Header background differs per distributor (white, blue, black...), so pick
// whichever of white / dark grey has the higher WCAG contrast against it.
@function channel-luminance($value) {
  $c: math.div($value, 255);
  @if $c <= 0.03928 {
    @return math.div($c, 12.92);
  }
  @return math.pow(math.div($c + 0.055, 1.055), 2.4);
}

@function luminance($color) {
  @return 0.2126 * channel-luminance(red($color))
    + 0.7152 * channel-luminance(green($color))
    + 0.0722 * channel-luminance(blue($color));
}

@function contrast-text($bg, $light: #ffffff, $dark: #1F2937) {
  $bg-lum: luminance($bg);
  $light-ratio: math.div(luminance($light) + 0.05, $bg-lum + 0.05);
  $dark-ratio: math.div($bg-lum + 0.05, luminance($dark) + 0.05);
  @return if($light-ratio >= $dark-ratio, $light, $dark);
}

$demo-text: contrast-text($header-bg);
$demo-text-dark: contrast-text($header-dark-bg);

.demo-disclaimer--desktop {
  color: $demo-text;
  font-weight: 600;
  font-size: 0.9rem;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  text-align: center;
  width: auto;
}

.demo-disclaimer-mobile {
  position: relative;
  display: none;
  align-items: center;
  flex-shrink: 0;
}

.demo-info-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  min-width: 2rem;
  padding: 0;
  border-radius: 50%;
  border: 1px solid rgba($demo-text, 0.75);
  background: transparent;
  color: $demo-text;
  box-shadow: none;
  cursor: pointer;

  &:hover,
  &:focus,
  &:active {
    background: rgba($demo-text, 0.15);
    border-color: $demo-text;
    color: $demo-text;
  }
}

.dark {
  .demo-disclaimer--desktop {
    color: $demo-text-dark;
  }

  .demo-info-btn {
    border-color: rgba($demo-text-dark, 0.75);
    color: $demo-text-dark;

    &:hover,
    &:focus,
    &:active {
      background: rgba($demo-text-dark, 0.15);
      border-color: $demo-text-dark;
      color: $demo-text-dark;
    }
  }
}

.demo-tooltip {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 220px;
  max-width: min(280px, 85vw);
  padding: 10px 12px;
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.35;
  text-align: center;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  z-index: 1050;
  color: #333333;
  background: #ffffff;
  white-space: normal;

  &::before {
    content: "";
    position: absolute;
    top: -6px;
    right: 12px;
    border: 6px solid transparent;
    border-bottom-color: #ffffff;
  }
}

@media (max-width: 768px) {
  .demo-disclaimer--desktop {
    display: none !important;
  }

  .demo-disclaimer-mobile {
    display: inline-flex;
  }
}

@media (min-width: 769px) {
  .demo-disclaimer-mobile {
    display: none !important;
  }
}
</style>
